import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * A right-hand slide-over panel.
 *
 * The counterpart to Modal for CONTENT rather than decisions. A modal is the
 * right shape for a short form or a confirmation — something you finish and
 * dismiss. A record you read, scroll and refer back to belongs in a panel
 * beside the list it came from, so the list stays visible and picking the next
 * person is one click rather than a round trip through a page.
 *
 * Behaviour, deliberately the same as Modal's so the two feel like one system:
 *   - Escape closes it, and so does a click on the backdrop.
 *   - Focus moves into the panel on open and returns to the trigger on close,
 *     so keyboard and screen-reader users are not left behind in the list.
 *   - `body` scrolling is locked, which works now the overlay is portalled to
 *     `body`: a wheel over the backdrop no longer finds DashboardLayout's
 *     scrollable `<main>` in its scroll chain.
 *
 * On a phone it takes the full width; from `sm` up it is a panel of `width`
 * hugging the right edge. Long content scrolls inside the body, so the header
 * and any footer stay put.
 *
 * It covers the WHOLE window, top bar included — see `offsetTop`. Reading a
 * record is a full-attention job, so the panel takes the screen rather than
 * leaving a strip of chrome above it competing for the eye.
 *
 * RENDERED THROUGH A PORTAL, like Modal, and for the same reason: a `fixed`
 * overlay left inside the page's own DOM subtree is subject to whatever
 * containing block its ancestors establish, and inside DashboardLayout it ends
 * up confined to the content area — the top bar and the left nav stay on top of
 * it, undimmed. Mounting on `document.body` makes it a true top-level layer, so
 * no z-index alone can fix this.
 *
 * @param {boolean}  isOpen
 * @param {Function} onClose
 * @param {node}     title     Panel heading. A node, so it can carry a badge.
 * @param {node}     subtitle  Small line under the title (an id, a status).
 * @param {node}     actions   Buttons for the header's right-hand side.
 * @param {node}     footer    Pinned to the bottom, outside the scroll area.
 * @param {string}   width     Tailwind max-width from `sm` up.
 * @param {string}   offsetTop Where the panel starts. Defaults to `top-0`, so
 *                             it covers the app's top bar too. Pass `top-14`
 *                             (the Navbar's height) to leave that bar visible.
 */
const SlideOver = ({
  isOpen,
  onClose,
  title,
  subtitle,
  actions,
  footer,
  width = 'sm:max-w-[560px]',
  offsetTop = 'top-0',
  children,
}) => {
  const panelRef = useRef(null);
  // Whatever had focus when we opened, so it can be given focus back.
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    returnFocusRef.current = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);

    // Lock body scrolling, for hosts where the body is the scroll container.
    // Restored exactly as it was, rather than blanked, so a page that sets its
    // own overflow keeps it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Wait for the panel to be laid out before moving focus into it.
    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      // Hand focus back to whatever opened the panel.
      const returnTo = returnFocusRef.current;
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      /* z-[80] clears everything the dashboard puts on screen: the Ask Patch
         overlay (z-40), the Navbar (z-50), the left Sidebar (z-[60]) and its
         collapsed flyout (z-[70]) — and stays BELOW Modal's z-[100], so a
         dialog opened from inside a panel still lands on top of it. */
      className={`fixed inset-x-0 bottom-0 ${offsetTop} z-[80] flex justify-end`}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop. Dimmer than a side panel's would be, because this one covers
          the whole window — a light wash over the top bar and nav reads as a
          rendering fault rather than a deliberate overlay. */}
      <div
        className="absolute inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-[2px] animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative h-full w-full ${width} bg-[#F8FAFC] dark:bg-[#1b1f26] shadow-[-8px_0_40px_rgba(15,23,42,0.16)] flex flex-col outline-none animate-in slide-in-from-right duration-300 ease-out`}
      >
        {/* Header — stays put while the body scrolls, so you always know whose
            record you are looking at. */}
        <div className="shrink-0 bg-white dark:bg-[#262b33] border-b border-[#E2E8F0] dark:border-gray-800 px-5 py-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-bold text-[#1E293B] dark:text-gray-100 tracking-tight truncate">
              {title}
            </div>
            {subtitle && (
              <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5 truncate">
                {subtitle}
              </div>
            )}
          </div>

          {actions}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="shrink-0 p-2 -mr-1 rounded-lg text-[#64748B] dark:text-gray-400 hover:bg-[#F1F5F9] dark:hover:bg-[#31373f] hover:text-[#1E293B] dark:hover:text-gray-200 transition"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5">
          {children}
        </div>

        {footer && (
          <div className="shrink-0 bg-white dark:bg-[#262b33] border-t border-[#E2E8F0] dark:border-gray-800 px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default SlideOver;
